import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import {
  NO_RESULTS_FILTERS,
  ResultsFilters,
  ResultsStatusFilter,
  ResultsTipFilter,
} from 'helpers/resultsDisplayHelper';
import styles from './ResultsFilterBar.module.css';

interface Props {
  value: ResultsFilters;
  onChange: (next: ResultsFilters) => void;
  /** Days present in the loaded results, newest first: [key, label]. */
  days?: [string, string][];
  shown: number;
  total: number;
}

const STATUSES: ResultsStatusFilter[] = ['all', 'ended', 'live', 'off'];
const TIPS: ResultsTipFilter[] = ['all', '1', 'X', '2'];

/** Narrow the results down: day, status, outcome and team. */
export const ResultsFilterBar = ({
  value,
  onChange,
  days,
  shown,
  total,
}: Props) => {
  const { t } = useTranslation();
  const set = (patch: Partial<ResultsFilters>) =>
    onChange({ ...value, ...patch });
  const active =
    value.day !== 'all' ||
    value.status !== 'all' ||
    value.tip !== 'all' ||
    value.team.trim() !== '';

  return (
    <div className={styles.bar}>
      <input
        type="search"
        className={styles.search}
        placeholder={t('results_filter_team_placeholder')}
        value={value.team}
        onChange={(e) => set({ team: e.target.value })}
      />
      {days && days.length > 1 && (
        <select
          className={styles.select}
          value={value.day}
          onChange={(e) => set({ day: e.target.value })}
          aria-label={t('date_label')}
        >
          <option value="all">{t('results_filter_all_days')}</option>
          {days.map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </select>
      )}
      <div className={styles.group} role="group">
        {STATUSES.map((s) => (
          <button
            key={s}
            type="button"
            className={classNames(styles.chip, {
              [styles.chipOn]: value.status === s,
            })}
            onClick={() => set({ status: s })}
          >
            {t(`results_filter_status_${s}`)}
          </button>
        ))}
      </div>
      <div className={styles.group} role="group" aria-label={t('tip_label')}>
        {TIPS.map((tip) => (
          <button
            key={tip}
            type="button"
            className={classNames(styles.chip, styles.tipChip, {
              [styles.chipOn]: value.tip === tip,
            })}
            onClick={() => set({ tip })}
          >
            {tip === 'all' ? t('results_filter_all_tips') : tip}
          </button>
        ))}
      </div>
      <span className={styles.count}>
        {active ? `${shown} / ${total}` : total}
      </span>
      {active && (
        <button
          type="button"
          className={styles.reset}
          onClick={() => onChange(NO_RESULTS_FILTERS)}
        >
          {t('results_filter_reset')}
        </button>
      )}
    </div>
  );
};
